import React, { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, TrendingUp, Package, DollarSign, ShoppingBag } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Analytics({ token }) {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/analytics`, { headers: { "X-Admin-Password": token }, params: { days } });
      setData(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [days]);

  if (loading && !data) return <div className="p-12 text-center bg-white rounded-2xl border border-[color:var(--dms-navy)]/10"><Loader2 className="w-6 h-6 animate-spin text-[color:var(--dms-peach)] mx-auto" /></div>;
  if (!data) return null;

  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={DollarSign} label="Revenue (period)" value={`$${data.period_revenue.toFixed(2)}`} />
        <Kpi icon={ShoppingBag} label="Paid orders" value={data.period_orders} />
        <Kpi icon={TrendingUp} label="Avg order value" value={`$${data.aov.toFixed(2)}`} />
        <Kpi icon={Package} label="Pending orders" value={data.pending_orders} />
      </div>
      <div className="mt-6 bg-white rounded-2xl border border-[color:var(--dms-navy)]/10 p-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div><p className="kicker">Trend</p><h3 className="font-serif text-xl text-[color:var(--dms-navy)] mt-1">Revenue by day</h3></div>
          <div className="flex items-center gap-2">
            {[7, 14, 30, 60, 90].map((d) => (
              <button key={d} onClick={() => setDays(d)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${days === d ? "bg-[color:var(--dms-navy)] text-white" : "bg-white border border-[color:var(--dms-navy)]/15 text-[color:var(--dms-navy)]"}`}>Last {d}d</button>
            ))}
          </div>
        </div>
        <RevenueChart points={data.daily} />
      </div>
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-[color:var(--dms-navy)]/10 p-6">
          <p className="kicker">All-time</p>
          <h3 className="font-serif text-xl text-[color:var(--dms-navy)] mt-1">Store totals</h3>
          <div className="mt-5 space-y-3 text-sm">
            <Row label="Lifetime revenue" value={`$${data.all_time_revenue.toFixed(2)}`} />
            <Row label="Lifetime paid orders" value={data.all_time_orders} />
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-[color:var(--dms-navy)]/10 p-6">
          <p className="kicker">Top products</p>
          <h3 className="font-serif text-xl text-[color:var(--dms-navy)] mt-1">Last {data.period_days} days</h3>
          <ul className="mt-5 divide-y divide-[color:var(--dms-navy)]/10">
            {data.top_products.length === 0 && <li className="py-4 text-sm text-[color:var(--dms-navy)]/60">No paid orders in this period yet.</li>}
            {data.top_products.map((p, i) => (
              <li key={i} className="py-3 flex items-center justify-between gap-3">
                <span className="text-[color:var(--dms-navy)] text-sm truncate">{p.title}</span>
                <span className="text-xs text-[color:var(--dms-navy)]/60 whitespace-nowrap">{p.qty} sold · ${p.revenue.toFixed(2)}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </>
  );
}

function Kpi({ icon: Icon, label, value }) {
  return (
    <div className="bg-white rounded-2xl border border-[color:var(--dms-navy)]/10 p-5">
      <div className="flex items-center gap-2 text-[color:var(--dms-peach)]"><Icon className="w-4 h-4" /><span className="kicker text-[10px]">{label}</span></div>
      <p className="font-serif text-3xl text-[color:var(--dms-navy)] mt-2">{value}</p>
    </div>
  );
}

function Row({ label, value }) {
  return <div className="flex items-center justify-between"><span className="text-[color:var(--dms-navy)]/60">{label}</span><span className="font-serif text-[color:var(--dms-navy)]">{value}</span></div>;
}

function RevenueChart({ points }) {
  if (!points || points.length === 0) return <p className="mt-6 text-sm text-[color:var(--dms-navy)]/60">No data yet.</p>;
  const w = 820, h = 240, pad = { l: 44, r: 10, t: 14, b: 28 };
  const max = Math.max(1, ...points.map((p) => p.revenue));
  const stepX = (w - pad.l - pad.r) / Math.max(1, points.length - 1);
  const toXY = (i, v) => [pad.l + i * stepX, pad.t + (h - pad.t - pad.b) * (1 - v / max)];
  const linePath = points.map((p, i) => { const [x, y] = toXY(i, p.revenue); return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`; }).join(" ");
  const areaPath = `${linePath} L${(pad.l + (points.length - 1) * stepX).toFixed(1)},${(h - pad.b).toFixed(1)} L${pad.l},${(h - pad.b).toFixed(1)} Z`;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((t) => ({ y: pad.t + (h - pad.t - pad.b) * (1 - t), v: max * t }));
  const everyNth = Math.max(1, Math.floor(points.length / 7));
  return (
    <div className="mt-5 overflow-x-auto">
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-auto" preserveAspectRatio="xMidYMid meet">
        <defs><linearGradient id="rev" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stopColor="#d4a574" stopOpacity="0.4" /><stop offset="100%" stopColor="#d4a574" stopOpacity="0" /></linearGradient></defs>
        {ticks.map((t, i) => (<g key={i}><line x1={pad.l} x2={w - pad.r} y1={t.y} y2={t.y} stroke="#1a2f4d14" /><text x={pad.l - 8} y={t.y + 4} fontSize="10" fill="#1a2f4d80" textAnchor="end">${t.v.toFixed(0)}</text></g>))}
        <path d={areaPath} fill="url(#rev)" />
        <path d={linePath} fill="none" stroke="#1a2f4d" strokeWidth="2" strokeLinecap="round" />
        {points.map((p, i) => { if (i % everyNth !== 0 && i !== points.length - 1) return null; const [x, y] = toXY(i, p.revenue); return (
          <g key={i}>
            <circle cx={x} cy={y} r="3" fill="#d4a574" stroke="#1a2f4d" strokeWidth="1" />
            <text x={x} y={h - 8} fontSize="10" fill="#1a2f4d80" textAnchor="middle">{p.date.slice(5)}</text>
          </g>
        ); })}
      </svg>
    </div>
  );
}
